import { getDeploymentConfig } from '../services/config';

/**
 * Checks if the current URL is running on localhost
 * @returns True if running on localhost or 127.0.0.1, false otherwise
 */
export const isLocalhost = (): boolean => {
  const hostname = window.location.hostname;

  return (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  );
};

/**
 * Gets the base domain from hostname parts
 * Extracts everything except the first subdomain (app/qodo-platform/etc)
 * Examples:
 *   - app.merge-pentest-1.st.codium.ai (5 parts) -> base domain: merge-pentest-1.st.codium.ai
 *   - app.qodo.ai (3 parts) -> base domain: qodo.ai
 *   - qodo-platform.dev.codium-inc.com (4 parts) -> base domain: dev.codium-inc.com
 *   - app.custom-company.com (3 parts) -> base domain: custom-company.com
 */
export const getBaseDomain = (hostname: string) => {
  const hostnameParts = hostname.split('.');

  if (hostnameParts.length <= 2) {
    return hostnameParts.join('.');
  }

  // For all domains with 3+ parts, take everything except the first part (the service subdomain)
  // This preserves environment-specific subdomains like "merge-pentest-1.st"
  return hostnameParts.slice(1).join('.');
};

/**
 * Returns all settings derived from the runtime DeploymentConfig.
 * All URL fields are computed from config.server_url and config.git_providers;
 * no build-time environment variables or settings.json keys are consulted at this layer.
 * @returns Environment settings with appropriate domain configuration
 */
export const getDynamicSettings = () => {
  const config = getDeploymentConfig();

  return {
    app_url: window.location.origin,
    server_url: config.server_url,
    auth_url_oidc: config.server_url,
    github_client_id_legacy: config.git_providers?.github?.client_id ?? '',
    github_client_id_oidc: config.git_providers?.github?.client_id ?? '',
    google_client_id_legacy: config.auth_providers?.google?.client_id ?? '',
    google_client_id_oidc: config.auth_providers?.google?.client_id ?? '',
    mixpanel_project_token: config.features?.mixpanel?.token ?? '',
    mixpanel_api_host: config.features?.mixpanel?.api_host ?? '',
    sentry_dsn: config.features?.sentry?.dsn ?? '',
  };
};

/**
 * Gets a specific setting value derived from the runtime DeploymentConfig.
 * @param key - The setting key to retrieve
 * @returns The setting value
 */
export const getDynamicSetting = <
  K extends keyof ReturnType<typeof getDynamicSettings>,
>(
  key: K,
): ReturnType<typeof getDynamicSettings>[K] => {
  return getDynamicSettings()[key];
};

/**
 * Returns the current app URL (always window.location.origin).
 * Using the browser origin directly ensures correctness across all deployment
 * topologies without requiring build-time or runtime URL configuration.
 * @returns The app URL for OAuth callbacks and sign-in/sign-out redirects
 */
export const getAppUrl = (): string => window.location.origin;
